import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  School, 
  Users, 
  Search,
  UserCog,
  Loader2,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [systemAdmins, setSystemAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [schoolsRes, usersRes, adminsRes] = await Promise.all([
          api.get('/schools'),
          api.get('/admin/users'),
          api.get('/admin/system-admins')
        ]);
        setSchools(schoolsRes.data);
        setUsers(usersRes.data);
        setSystemAdmins(adminsRes.data);
      } catch (error) {
        toast.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleImpersonate = async (userId) => {
    try {
      await impersonate(userId);
      toast.success('Now impersonating user');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to impersonate user');
    }
  };

  const filteredUsers = users.filter(u => 
    u.role !== 'system_admin' && (
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Count suspended schools
  const suspendedSchools = schools.filter(s => s.is_suspended).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900">System Administration</h1>
          <p className="text-slate-500 mt-1">Manage schools and users across the platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/schools')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Schools</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{schools.length}</p>
                  {suspendedSchools > 0 && (
                    <p className="text-xs text-amber-600 mt-1">{suspendedSchools} suspended</p>
                  )}
                </div>
                <div className="bg-indigo-500 p-3 rounded-lg">
                  <School className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{users.length}</p>
                </div>
                <div className="bg-emerald-500 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">School Users</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {users.filter(u => u.role !== 'system_admin').length}
                  </p>
                </div>
                <div className="bg-amber-500 p-3 rounded-lg">
                  <UserCog className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/admin/system-admins')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">System Admins</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{systemAdmins.length}</p>
                  <p className="text-xs text-indigo-600 mt-1 flex items-center">Manage <ChevronRight className="h-3 w-3" /></p>
                </div>
                <div className="bg-purple-500 p-3 rounded-lg">
                  <ShieldCheck className="h-6 w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Impersonation */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">User Impersonation</CardTitle>
            <CardDescription>Access a user's account to provide support</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-users-input"
                />
              </div>
            </div>
            
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No users found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>MFA</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.slice(0, 10).map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{user.full_name || user.username}</p>
                          <p className="text-sm text-slate-500">{user.username}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'school_admin' ? 'default' : 'secondary'}>
                          {user.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.mfa_enabled ? 'default' : 'outline'}>
                          {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleImpersonate(user.id)}
                          data-testid={`impersonate-${user.id}`}
                        >
                          <UserCog className="h-4 w-4 mr-2" />
                          Impersonate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;
